import { Injectable } from "@angular/core";
import "firebase/firestore";
import "firebase/auth";
import "firebase/storage";
import { ModalController, NavController } from "@ionic/angular";
import { UserService } from "./user.service";
import { MenuItem } from "../models/menuItem";
import { isNativeIos } from "../utils/browser-support";
import { EntitlementService } from "./entitlement.service";
import { AskFlaggerModalPage } from "src/app/shared/modals/ask-flagger-modal/ask-flagger-modal";
import { AskFlaggerContextOptions } from "./ask-flagger.service";

@Injectable({
	providedIn: "root",
})
export class MenuService {
	public scrollToViewId: string = "";
	public isOwner: boolean = false;
	public isRider: boolean = true;
	private readonly accountShellRoutePrefixes = [
		"/dashboard",
		"/account",
		"/subscription-access",
		"/race-results",
		"/entries",
		"/live-stream",
		"/lap-times",
		"/cart",
		"/video-clips",
		"/admin",
		"/payment-settings",
		"/order-status",
	];
	constructor(
		public navCtrl: NavController,
		public userService: UserService,
		private entitlementService: EntitlementService,
		private modalCtrl: ModalController
	) { }

	async scroll(to: string) {
		this.scrollToViewId = to;
		let naved = await this.navCtrl.navigateRoot("");
		if (naved === null) {
			this.scrollToView();
		}
	}

	public menuItems(): MenuItem[] {
		if (isNativeIos()) {
			return this.nativeIosMenuItems();
		}

		let menu: MenuItem[] = [
			new MenuItem("Rider", "", false),
			new MenuItem("Owner", "owner", false),
			new MenuItem("Hardware", "hardware", false),
		];
		if (this.userService.loggedIn) {
			menu.push(new MenuItem("Account", "account", false));
			menu.push(new MenuItem("Ask Flagger", "askFlagger", false));
			menu.push(new MenuItem("Events", "dashboard", false));
			menu.push(new MenuItem("Entries", "entries", false));
			menu.push(new MenuItem("Official Results", "race-results", false));
			this.addCentralEventTabs(menu, "main-menu");
		} else {
			menu.push(new MenuItem("Login", "login", false));
		}
		return menu;
	}

	public accountMenuItems(): MenuItem[] {
		const menu: MenuItem[] = [
			new MenuItem("Account", "account", false),
			new MenuItem("Ask Flagger", "askFlagger", false),
			new MenuItem("Events", "dashboard", false),
			new MenuItem("Entries", "entries", false),
			new MenuItem("Official Results", "race-results", false),
		];

		this.addCentralEventTabs(menu, "account-menu");
		if (!isNativeIos() && this.userService.loggedInUser?.role === "admin") {
			menu.push(new MenuItem("Admin", "admin", false));
		}

		return menu;
	}

	public isAccountShellRoute(url: string): boolean {
		const cleanPath = this.cleanRoutePath(url);
		return this.accountShellRoutePrefixes.some(
			(prefix) => cleanPath === prefix || cleanPath.startsWith(`${prefix}/`),
		);
	}

	public isActiveAccountRoute(page: string, url: string): boolean {
		const cleanPath = this.cleanRoutePath(url);
		if (page === "askFlagger") {
			return false;
		}
		const pagePath = `/${page}`.replace(/\/+$/, "");
		return cleanPath === pagePath || cleanPath.startsWith(`${pagePath}/`);
	}

	public ownersMenu(): MenuItem[] {
		if (isNativeIos()) {
			return [];
		}

		let menu: MenuItem[] = [
			new MenuItem("Pricing", "pricing/owners", false),
			new MenuItem("Track ROI", "owner/roi", false),
		];
		return menu;
	}
	public ridersMenu(): MenuItem[] {
		if (isNativeIos()) {
			return [];
		}

		let menu: MenuItem[] = [
			new MenuItem("Pricing", "pricing/riders", false),
			new MenuItem("Features", "features", false),
			new MenuItem("Merch", "merch", false),
		];
		return menu;
	}

	navigateTo(page: string) {
		if (isNativeIos() && this.isNativeIosBlockedPage(page)) {
			this.navCtrl.navigateRoot(
				this.userService.loggedIn ? "dashboard" : "login",
			);
			return;
		}

		if (page === "hardware") {
			this.openFlaggerStore();
			return;
		}
		if (page === "askFlagger") {
			void this.openAskFlagger();
			return;
		}
		if (
			page === "owner" ||
			page === "pricing/owners" ||
			page === "owner/pricing" ||
			page === "owner/roi"
		) {
			this.isOwner = true;
			this.isRider = false;
		} else {
			this.isRider = true;
			this.isOwner = false;
		}
		if (page === "") {
			this.navCtrl.navigateRoot("");
		} else if (page != "") {
			this.navCtrl.navigateForward([page]);
		}
	}

	private openFlaggerStore() {
		const privacyUrl = "https://flaggerai.square.site/";
		window.open(privacyUrl, "_blank");
	}

	async openAskFlagger(options: AskFlaggerContextOptions = {}) {
		const modal = await this.modalCtrl.create({
			component: AskFlaggerModalPage,
			componentProps: options,
			cssClass: "fullscreen-modal-css",
		});

		await modal.present();
		return modal;
	}

	private nativeIosMenuItems(): MenuItem[] {
		if (!this.userService.loggedIn) {
			return [new MenuItem("Login / Signup", "login", false)];
		}

		let menu: MenuItem[] = [
			new MenuItem("Dashboard", "dashboard", false),
			new MenuItem("Account", "account", false),
			new MenuItem("Ask Flagger", "askFlagger", false),
			new MenuItem("Entries", "entries", false),
			new MenuItem("Official Results", "race-results", false),
		];

		this.addCentralEventTabs(menu, "native-ios-menu");

		return menu;
	}

	private addCentralEventTabs(menu: MenuItem[], debugRoute: string) {
		const entitlements =
			this.entitlementService.getCentralEventTabEntitlements(
				this.userService.loggedInUser,
				debugRoute
			);

		if (
			entitlements.canViewPreliminaryResults ||
			entitlements.canViewLapTimes
		) {
			menu.push(new MenuItem("Lap Times", "lap-times", false));
		}
		if (entitlements.canViewLiveStream) {
			menu.push(new MenuItem("Live Stream", "live-stream", false));
		}
		if (entitlements.canViewVideoClips) {
			menu.push(new MenuItem("Video Clips", "video-clips", false));
		}
	}

	private isNativeIosBlockedPage(page: string): boolean {
		return [
			"",
			"home",
			"landing",
			"demo",
			"owner",
			"hardware",
			"pricing",
			"pricing/riders",
			"pricing/owners",
			"features",
			"solutions",
			"riders",
			"track-owners",
			"promoters",
			"safety",
			"merch",
			"owner/pricing",
			"owner/roi",
			"admin",
			"payment-settings",
			"cart",
		].includes(page);
	}

	scrollToView() {
		try {
			var anchor = document.getElementById(this.scrollToViewId);
			if (anchor) {
				anchor.scrollIntoView({ block: "start", behavior: "smooth" });
				anchor.scrollTo();
			}
		} catch (error) { }
		this.scrollToViewId = "";
	}

	private cleanRoutePath(url: string): string {
		return (url || "/").split(/[?#;]/)[0] || "/";
	}
}
